/**
 * mulberry32 — a 32-bit seeded PRNG.
 *
 * Chosen because it is tiny, has no dependencies, passes gjrand's smallcrush,
 * and — crucially — carries its whole state in one int32, so a run can be saved
 * mid-stream and resumed exactly. The `| 0` on the counter is not decoration:
 * without it the accumulator drifts past 2^53 after a few million draws and the
 * stream silently stops being reproducible.
 */
const INCREMENT = 0x6d2b79f5;
const UINT32_RANGE = 0x1_0000_0000;
const scramble = (cursor) => {
    let t = Math.imul(cursor ^ (cursor >>> 15), cursor | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
};
const assertIntegerRange = (minInclusive, maxExclusive) => {
    if (!Number.isInteger(minInclusive) || !Number.isInteger(maxExclusive)) {
        throw new RangeError(`Rng.int expects integer bounds, received [${minInclusive}, ${maxExclusive})`);
    }
    if (maxExclusive <= minInclusive) {
        throw new RangeError(`Rng.int expects a non-empty range, received [${minInclusive}, ${maxExclusive})`);
    }
};
/**
 * Resumes a stream from a previously captured cursor. The continuation is
 * bit-identical to the stream the cursor came from.
 */
export const restoreRng = (state) => {
    if (!Number.isFinite(state)) {
        throw new RangeError(`Rng state must be a finite number, received ${String(state)}`);
    }
    let cursor = state | 0;
    const next = () => {
        cursor = (cursor + INCREMENT) | 0;
        return scramble(cursor) / UINT32_RANGE;
    };
    return {
        next,
        int: (minInclusive, maxExclusive) => {
            assertIntegerRange(minInclusive, maxExclusive);
            return minInclusive + Math.floor(next() * (maxExclusive - minInclusive));
        },
        state: () => cursor,
    };
};
/** Creates an independent stream. The same seed always yields the same sequence. */
export const createRng = (seed) => restoreRng(seed);
//# sourceMappingURL=rng.js.map